import React, { useEffect, useState } from "react";
import "./CreateCustomWordHit.css";
import { createWordHitLink } from "../utils/branchClient";
import * as amplitude from "@amplitude/unified";
import { logEvent } from "firebase/analytics";
import { analytics } from "../utils/firebase";

function CreateCustomWordHit({ onClose, langString = "en" }) {
  const [theme, setTheme] = useState("");
  const [word1, setWord1] = useState("");
  const [word2, setWord2] = useState("");
  const [word3, setWord3] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [created, setCreated] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchError, setBranchError] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [shareFeedback, setShareFeedback] = useState("");

  const maxLength = 7;

  const word1TooLong = word1.trim().length > maxLength;
  const word2TooLong = word2.trim().length > maxLength;
  const word3TooLong = word3.trim().length > maxLength;

  const isThemeFilled = theme.trim().length > 0;
  const isWord1Filled = word1.trim().length > 0;
  const isWord2Filled = word2.trim().length > 0;
  const isWord3Filled = word3.trim().length > 0;
  const isCreatorFilled = creatorName.trim().length > 0;

  const texts =
    langString === "nl"
      ? {
          title: "Maak je eigen WordHit!",
          subtitle:
            "Deel je eigen WordHit met vrienden! Verzin een thema en drie bijpassende woorden. De puzzel wordt automatisch voor je gemaakt.",
          themeLabel: "Thema",
          themePlaceholder: "Thema",
          wordLabel: (index) => `Woord ${index}`,
          wordPlaceholder: (index) => `Woord ${index}`,
          hint: "max. 7 letters",
          create: "Maak WordHit",
          creating: "Bezig...",
          share: "Deel",
          validationMissing: "Vul het thema, alle woorden en je voornaam in.",
          validationTooLong: "Woorden mogen maximaal 7 letters bevatten.",
          branchError: "Deel-link maken is mislukt. Controleer de Branch-configuratie.",
          shareShared: "Link gedeeld.",
          shareCopied: "Link gekopieerd naar klembord.",
          shareOpened: "Link geopend in een nieuw tabblad.",
          shareError: "Delen mislukt. Probeer het opnieuw.",
          noLink: "Er is nog geen link beschikbaar om te delen.",
          createShare: "Maak & Deel",
          creatorLabel: "Jouw voornaam",
          creatorPlaceholder: "Voornaam",
          wordsGroupLabel: "3 woorden, maximaal 7 letters",
        }
      : {
          title: "Create your own WordHit!",
          subtitle:
            "Share your custom WordHit with friends! Pick a theme and three matching words—the puzzle will be generated for you.",
          themeLabel: "Theme",
          themePlaceholder: "Theme",
          wordLabel: (index) => `Word ${index}`,
          wordPlaceholder: (index) => `Word ${index}`,
          hint: "max 7 characters",
          create: "Create",
          creating: "Working...",
          share: "Share",
          validationMissing: "Enter the theme, all three words, and your first name.",
          validationTooLong: "Words can contain at most 7 letters.",
          branchError: "Couldn't create share link. Check the Branch configuration.",
          shareShared: "Link shared.",
          shareCopied: "Link copied to clipboard.",
          shareOpened: "Link opened in a new tab.",
          shareError: "Sharing failed. Please try again.",
          noLink: "There's no link available to share yet.",
          createShare: "Create & Share",
          creatorLabel: "Your first name",
          creatorPlaceholder: "First name",
          wordsGroupLabel: "3 words, max 7 letters",
        };

  const missingFields =
    !isThemeFilled ||
    !isWord1Filled ||
    !isWord2Filled ||
    !isWord3Filled ||
    !isCreatorFilled;
  const anyTooLong = word1TooLong || word2TooLong || word3TooLong;

  const isButtonDisabled =
    missingFields ||
    anyTooLong ||
    branchLoading;

  const resetStatusMessages = () => {
    setBranchError("");
    setShareFeedback("");
  };

  const createButtonPRessed = async () => {
    resetStatusMessages();
    setBranchLoading(true);
    try {
      const link = await createWordHitLink({
        theme: theme.trim(),
        words: [word1, word2, word3],
        creator: creatorName.trim(),
        lang: langString,
      });
      if (!link) {
        setBranchError(texts.branchError);
        setShareUrl("");
        return link;
      }
      const decoratedLink = appendLangParam(link);
      setShareUrl(decoratedLink);
      try {
        amplitude.track("custom_wordhit_created", {
          theme: theme.trim(),
          words: [word1.trim(), word2.trim(), word3.trim()],
          creator: creatorName.trim(),
          lang: langString,
        });
        try {
          if (analytics) {
            logEvent(analytics, "custom_wordhit_created", {
              theme: theme.trim(),
              word_count: 3,
              lang: langString,
            });
          }
        } catch (analyticsError) {
          console.warn("custom WordHit firebase event failed", analyticsError);
        }
      } catch (eventError) {
        console.warn("custom WordHit event failed", eventError);
      }
      return decoratedLink;
    } catch (error) {
      console.error("Create WordHit link error:", error);
      setBranchError(texts.branchError);
      setShareUrl("");
      return null;
    } finally {
      setBranchLoading(false);
    }
  };

  const appendLangParam = (url) => {
    if (!url || langString !== "nl") {
      return url;
    }
    try {
      const parsed = new URL(url);
      parsed.searchParams.set("lang", "nl");
      return parsed.toString();
    } catch (err) {
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}lang=nl`;
    }
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
    resetStatusMessages();
  };

  const handleCreateClick = async () => {
    if (isButtonDisabled) {
      return;
    }
    const link = await createButtonPRessed();
    setCreated(true);
    if (!link) {
      setShareUrl("");
      return;
    }
    await handleShareClick(link);
  };

  const handleShareClick = async (linkOverride) => {
    resetStatusMessages();
    const urlToShare = linkOverride || shareUrl;
    if (!urlToShare) {
      setBranchError(texts.noLink);
      return;
    }

    try {
      if (navigator.share) {
        await navigator.share({
        //   title:
        //     langString === "nl"
        //       ? `Speel mijn WordHit: ${theme.trim()}`
        //       : `Play my WordHit: ${theme.trim()}`,
          text:
            langString === "nl"
              ? `Speel mijn zelfgemaakte WordHit! Thema: ${theme.trim()}`
              : `Play my self-created WordHit! Theme: ${theme.trim()}`,
          url: urlToShare,
        });
        //setShareFeedback(texts.shareShared);
        return;
      }

      if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${urlToShare}`);
        setShareFeedback(texts.shareCopied);
        return;
      }

      window.open(urlToShare, "_blank", "noopener,noreferrer");
      setShareFeedback(texts.shareOpened);
    } catch (error) {
      console.error("Share WordHit link error:", error);
      setBranchError(texts.shareError);
    }
  };

  return (
    <>
      <div className="custom-create-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          onClose();
        }
      }} />
      <div className="custom-create-modal">
        <button
          className="custom-create-close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
        <div className="custom-create-container">
          <h1 className="custom-create-title">{texts.title}</h1>
          <p className="custom-create-subtitle">{texts.subtitle}</p>

          <div className="custom-create-form">
            <label className="custom-create-label">
              {texts.themeLabel}
              <input
                className="custom-create-input"
                type="text"
                value={theme}
                onChange={handleInputChange(setTheme)}
                placeholder={texts.themePlaceholder}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              <span className="custom-create-hint" />
            </label>
            <div className="custom-create-group">
              <div className="custom-create-group-label">
                {texts.wordsGroupLabel}
              </div>
              <div className="custom-create-word-row">
                <input
                  className={`custom-create-input custom-create-word-input ${
                    word1TooLong ? "custom-create-input-error" : ""
                  }`}
                  type="text"
                  value={word1}
                  onChange={handleInputChange(setWord1)}
                  placeholder={texts.wordPlaceholder(1)}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <input
                  className={`custom-create-input custom-create-word-input ${
                    word2TooLong ? "custom-create-input-error" : ""
                  }`}
                  type="text"
                  value={word2}
                  onChange={handleInputChange(setWord2)}
                  placeholder={texts.wordPlaceholder(2)}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <input
                  className={`custom-create-input custom-create-word-input ${
                    word3TooLong ? "custom-create-input-error" : ""
                  }`}
                  type="text"
                  value={word3}
                  onChange={handleInputChange(setWord3)}
                  placeholder={texts.wordPlaceholder(3)}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
            </div>
            <label className="custom-create-label">
              {texts.creatorLabel}
              <input
                className="custom-create-input"
                type="text"
                value={creatorName}
                onChange={handleInputChange(setCreatorName)}
                placeholder={texts.creatorPlaceholder}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              <span className="custom-create-hint" />
            </label>
          </div>

          <button
            className="custom-create-button"
            onClick={handleCreateClick}
            disabled={isButtonDisabled}
          >
            {branchLoading ? texts.creating : texts.createShare}
          </button>

          {branchError && (
            <div className="custom-create-alert custom-create-alert-error">
              {branchError}
            </div>
          )}

          {shareFeedback && !branchError && (
            <div className="custom-create-feedback">{shareFeedback}</div>
          )}

          {shareUrl && created && <></>}
        </div>
      </div>
    </>
  );
}

export default CreateCustomWordHit;

